import { useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { Camera, Save, User, Mail, Phone, X, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { useUser } from "@/contexts/UserContext";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { compressImage } from "@/utils/imageCompression";

interface ProfileFormData {
  fullName: string;
  email: string;
}

export default function Profile() {
  const { user, updateProfile } = useUser();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'he';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [profileImage, setProfileImage] = useState<string | null>(user?.profileImage || null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<ProfileFormData>({
    defaultValues: {
      fullName: user?.fullName || "",
      email: user?.email || ""
    }
  });

  if (!user) {
    return <div>Loading...</div>;
  }

  // Generate initials for avatar fallback
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const initials = user.fullName ? getInitials(user.fullName) : user.phoneNumber.slice(-2);

  const handleImageSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError(t('profile.invalidImage'));
      return;
    }

    // Validate file size (max 10MB before compression)
    if (file.size > 10 * 1024 * 1024) {
      setError(t('profile.imageTooLarge'));
      return;
    }

    setImageFile(file);
    setError("");

    try {
      // Compress image before preview and upload
      const compressedImage = await compressImage(file, 800, 800, 0.8);
      setProfileImage(compressedImage);
    } catch (error) {
      console.error('Error compressing image:', error);
      setError(t('profile.imageCompressionFailed') || 'Failed to process image');
    }
  };

  const handleImageRemove = () => {
    setProfileImage(null);
    setImageFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const onSubmit = async (data: ProfileFormData) => {
    setError("");
    setLoading(true);

    try {
      await updateProfile({
        fullName: data.fullName.trim(),
        email: data.email.trim(),
        profileImage: profileImage
      });

      toast.success(t('profile.profileUpdated'));
      
    } catch (error: any) {
      console.error('Error updating profile:', error);
      setError(error.message || t('profile.updateFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`container max-w-4xl mx-auto p-6 page-shell`} dir={isRTL ? 'rtl' : 'ltr'} style={{ textAlign: isRTL ? 'right' : 'left' }}>
      <div className="page-heading">
        <h1 className="text-3xl font-bold">{t('profile.title')}</h1>
        <p className="text-muted-foreground">
          {t('profile.subtitle')}
        </p>
      </div>

      <Card dir={isRTL ? 'rtl' : 'ltr'} style={{ textAlign: isRTL ? 'right' : 'left' }} className="border-border/90">
        <CardHeader>
          <CardTitle className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
            <User className="h-5 w-5" />
            {t('profile.profileInformation')}
          </CardTitle>
          <CardDescription>
            {t('profile.updateInfo')}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Profile Picture Section */}
          <div className="space-y-4" style={{ textAlign: isRTL ? 'right' : 'left' }}>
            <Label className="block">{t('profile.profilePicture')}</Label>
            <div className={`flex items-start gap-4 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
              <Avatar className={`h-20 w-20 ${isRTL ? 'order-last' : ''}`}>
                <AvatarImage src={profileImage || undefined} alt="Profile" />
                <AvatarFallback className="text-lg font-medium">
                  {initials}
                </AvatarFallback>
              </Avatar>
              
              <div className={`flex flex-col gap-2 ${isRTL ? 'items-end' : 'items-start'} flex-1`}>
                <div className={`flex gap-2 flex-wrap ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}
                  >
                    <Camera className="h-4 w-4" />
                    {t('profile.changePhoto')}
                  </Button>
                  
                  {profileImage && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleImageRemove}
                      className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}
                    >
                      <X className="h-4 w-4" />
                      {t('profile.remove')}
                    </Button>
                  )}
                </div>
                
                <p className="text-xs text-muted-foreground">
                  {t('profile.imageHelp')}
                </p>
              </div>
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                className="hidden"
              />
            </div>
          </div>

          <Separator />

          {/* Profile Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fullName" className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
                  <User className="h-4 w-4" />
                  {t('profile.fullName')}
                </Label>
                <Input
                  id="fullName"
                  {...register("fullName", {
                    required: t('profile.fullNameRequired'),
                    minLength: {
                      value: 2,
                      message: t('profile.nameMinLength')
                    }
                  })}
                  className={errors.fullName ? "border-destructive" : ""}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  style={{ textAlign: isRTL ? 'right' : 'left' }}
                />
                {errors.fullName && (
                  <p className="text-sm text-destructive">{errors.fullName.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
                  <Mail className="h-4 w-4" />
                  {t('profile.emailAddress')}
                </Label>
                <Input
                  id="email"
                  type="email"
                  {...register("email", {
                    required: t('profile.emailRequired'),
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: t('profile.invalidEmail')
                    }
                  })}
                  className={errors.email ? "border-destructive" : ""}
                  dir="ltr"
                  style={{ textAlign: 'left' }}
                />
                {errors.email && (
                  <p className="text-sm text-destructive">{errors.email.message}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
                <Phone className="h-4 w-4" />
                {t('profile.phoneNumber')}
              </Label>
              <Input
                value={user.phoneNumber}
                disabled
                className="bg-muted"
                dir="ltr"
                style={{ textAlign: 'left' }}
              />
              <p className="text-xs text-muted-foreground">
                {t('profile.phoneCannotChange')}
              </p>
            </div>

            <div className="space-y-2">
              <Label className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
                <Calendar className="h-4 w-4" />
                {t('profile.memberSince')}
              </Label>
              <Input
                value={user.joinDate ? new Date(user.joinDate).toLocaleDateString() : t('profile.unknown')}
                disabled
                className="bg-muted"
                dir={isRTL ? 'rtl' : 'ltr'}
                style={{ textAlign: isRTL ? 'right' : 'left' }}
              />
              <p className="text-xs text-muted-foreground">
                {t('profile.joinDateHelp')}
              </p>
            </div>

            <div className={`flex pt-4 ${isRTL ? 'justify-start' : 'justify-end'}`}>
              <Button type="submit" disabled={loading} className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                {loading ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-background border-t-transparent" />
                    {t('profile.saving')}
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    {t('profile.saveChanges')}
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
} 